import React, { useContext, useEffect, useState } from 'react';
import CallTableViewModel from '../../../ViewModels/CallTableViewModel';
import { APIContext } from '../../../Contexts/APIContext';
import GdprFiltersViewModel from './ViewModel/GDPRFiltersViewModel';
import { useSnackbar } from 'notistack';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface IFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function GdprDialog(props: IFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [hasGPDR, setHasGPDR] = useState<boolean>(false);
    const [templateOptions, setTemplateOptions] = useState<string[]>([]);
    const [questionOptions, setQuestionOptions] = useState<string[]>([]);
    const [gdprFilter, setGdprFilter] = useState<GdprFiltersViewModel>(props.callTableViewModel.filters.gdprFilters);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (props.open) {
            setGdprFilter(props.callTableViewModel.filters.gdprFilters);
        }
    }, [props.open])

    useEffect(() => {
        hasGDPROptions();
    }, []);

    useEffect(() => {
        if (gdprFilter.channel !== '') getQuestionOptions();
    }, [gdprFilter.channel]);

    useEffect(() => {
        if (gdprFilter.category !== '') getTemplateOptions();
    }, [gdprFilter.category]);

    //#region database calls
    const hasGDPROptions = () => {
        get('AIAnalysis', `GetClientContiansGDPROptions?client=` + props.callTableViewModel.filters.client, (data) => {
            setHasGPDR(data);
        });
    }

    const getTemplateOptions = () => {
        get('AIAnalysis', `GetGDPRTemplateOptions?client=` + props.callTableViewModel.filters.client + '&category=' + gdprFilter.category, (data) => {
            setTemplateOptions(data);
        });
    }

    const getQuestionOptions = () => {
        get('AIAnalysis', `GetGDPRQuestionOptions?client=` + props.callTableViewModel.filters.client + `&channel=` + gdprFilter.channel + '&category=' + gdprFilter.category, (data) => {
            setQuestionOptions(data)
        });
    }
    //#endregion

    //#region Handlers
    const HandleCategorySelect = (value: string) => {
        // Clear Channel, Question, and Answer values (as the list will change)
        setGdprFilter({
            ...gdprFilter,
            category: value,
            channel: "",
            question: "",
            answer: ""
        });
    }

    const HandleChannelSelect = (value: string) => {
        // Cascascade clear dependent dropdowns
        setGdprFilter({
            ...gdprFilter,
            channel: value,
            question: "",
            answer: ""
        });
    }

    const HandleQuestionSelect = (value: string) => {
        // Cascascade clear dependent dropdowns
        setGdprFilter({
            ...gdprFilter,
            question: value,
            answer: ""
        });
    }

    const HandleAnswerSelect = (value: string) => {
        setGdprFilter({
            ...gdprFilter,
            answer: value
        });
    }
    //#endregion

    const Confirm = () => {
        if (gdprFilter.channel !== '' && gdprFilter.question !== '' && gdprFilter.answer !== '') {
            const newCallTableViewModel = { ...props.callTableViewModel };

            newCallTableViewModel.filters.gdprFilters = gdprFilter;

            props.setCallTableViewModel({
                ...newCallTableViewModel
            })
            props.setOpen(false);
        } else {
            enqueueSnackbar("All values must be selected to confirm", { variant: 'warning' });
        }
    }

    return (
        <Dialog onClose={() => props.setOpen(false)} open={props.open} fullWidth maxWidth="sm">
            <DialogTitle>PII / GDPR </DialogTitle>
            <DialogContent>
                {hasGPDR ?
                    <Grid container style={{ width: '100%' }}>
                        <Grid size={12}>
                            <InputLabel>Category</InputLabel>
                            <TextField
                                select
                                name="category"
                                value={gdprFilter.category}
                                onChange={(e) => HandleCategorySelect(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value={'GDPR'}>GDPR</MenuItem>
                                <MenuItem value={'PII'}>PII</MenuItem>
                            </TextField>
                        </Grid>
                        {gdprFilter.category !== '' &&
                            <Grid size={12}>
                                <InputLabel>Channel</InputLabel>
                                {templateOptions.length > 0 ?
                                    <TextField
                                        select
                                        name="channel"
                                        value={gdprFilter.channel}
                                        onChange={(e) => HandleChannelSelect(e.target.value)}
                                        fullWidth
                                    >
                                        {templateOptions.map((template: string) => {
                                            return (<MenuItem key={template} value={template}>{template}</MenuItem>);
                                        })}
                                    </TextField>
                                    :
                                    <p>This client does not use the selected category</p>
                                }
                            </Grid>
                        }
                        {gdprFilter.channel !== '' &&
                            <Grid size={12}>
                                <InputLabel>Question</InputLabel>
                                <TextField
                                    select
                                    name="question"
                                    value={gdprFilter.question}
                                    onChange={(e) => HandleQuestionSelect(e.target.value)}
                                    fullWidth
                                    disabled={gdprFilter.channel === '' || gdprFilter.category === ''}
                                >
                                    {questionOptions.length > 0 && questionOptions.map((question: string) => {
                                        return (<MenuItem key={question} value={question}>{question}</MenuItem>);
                                    })}
                                </TextField>
                            </Grid>
                        }
                        {gdprFilter.channel !== '' && gdprFilter.question !== '' && gdprFilter.category !== '' &&
                            <Grid size={12}>
                                <InputLabel>Answer</InputLabel>
                                <TextField
                                    select
                                    name="answer"
                                    value={gdprFilter.answer}
                                    onChange={(e) => HandleAnswerSelect(e.target.value)}
                                    fullWidth
                                    disabled={gdprFilter.channel === '' || gdprFilter.question === '' || gdprFilter.category === ''}
                                >
                                    <MenuItem key={0} value="Yes">Yes</MenuItem>
                                    <MenuItem key={1} value="No">No</MenuItem>
                                    <MenuItem key={2} value="N/A">N/A</MenuItem>
                                </TextField>
                            </Grid>
                        }
                    </Grid>
                    :
                    <p>This AI Filter Is Not Used By This Client</p>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.setOpen(false)} color="primary">Cancel</Button>
                {templateOptions.length > 0 &&
                    <Button onClick={Confirm} color="primary">Confirm</Button>
                }
            </DialogActions>
        </Dialog>
    )
}